// Cellery Soapbox — workspace (CONTRACTS §5/§11).
// Live list of folders + docs the signed-in user may access. Folders nest; the
// tree is built client-side from parentId. Users can browse into folders, upload
// docs, create/rename/move/delete folders, and (owners) share folders. All
// mutations go through the Admin-SDK endpoints (folders.ts / upload.ts).

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth";
import {
  formatRelative,
  formatTimestamp,
  listMyDocs,
  listMyFolders,
} from "../firestore";
import {
  createFolder,
  deleteFolder,
  duplicateDoc,
  renameItem,
  setFolderAccess,
} from "../folders";
import type { DocMeta, FolderMeta } from "../types";
import { Icon } from "../components/Icon";
import { Menu, type MenuItem } from "../components/Menu";
import { Modal } from "../components/Modal";
import { FolderTree } from "../components/FolderTree";
import { Breadcrumb, type Crumb } from "../components/Breadcrumb";
import { UploadDialog } from "../components/UploadDialog";
import { MoveDialog } from "../components/MoveDialog";
import { SharePanel } from "../components/SharePanel";
import { ConfidentialBadge } from "../components/ConfidentialBadge";
import { CardSkeletons } from "../components/Spinner";
import { useToast } from "../components/Toast";
import { useIsPhone } from "../hooks/useMediaQuery";

type Sort = "recent" | "name" | "views" | "viewed";

/** Folders carry no view stats, so they fall back to recency for those sorts. */
const docSortValue = (d: DocMeta, sort: Sort): number => {
  if (sort === "views") return d.stats?.views ?? 0;
  if (sort === "viewed") return d.stats?.lastViewedAt?.toMillis?.() ?? 0;
  return d.updatedAt?.toMillis?.() ?? 0;
};

export function DocList() {
  const { emailLower } = useAuth();
  const navigate = useNavigate();
  const { folderId: folderParam } = useParams<{ folderId?: string }>();
  const currentFolderId = folderParam ?? null;
  const { error: toastError, success } = useToast();

  const [docs, setDocs] = useState<DocMeta[]>([]);
  const [folders, setFolders] = useState<FolderMeta[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [loadingFolders, setLoadingFolders] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // On phone the folder rail is an off-canvas drawer.
  const isPhone = useIsPhone();
  const [railOpen, setRailOpen] = useState(false);
  // Close the drawer when navigating into a folder or when leaving phone width.
  useEffect(() => setRailOpen(false), [currentFolderId]);
  useEffect(() => {
    if (!isPhone) setRailOpen(false);
  }, [isPhone]);

  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<Sort>("recent");

  const [uploadOpen, setUploadOpen] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState<
    { kind: "doc" | "folder"; id: string; name: string; parentId: string | null } | null
  >(null);
  const [renameTarget, setRenameTarget] = useState<
    { kind: "doc" | "folder"; id: string; name: string } | null
  >(null);
  const [shareFolder, setShareFolder] = useState<FolderMeta | null>(null);

  useEffect(() => {
    if (!emailLower) return;
    const unsubDocs = listMyDocs(
      emailLower,
      (next) => {
        setDocs(next);
        setLoadingDocs(false);
      },
      () => {
        setError("Could not load your documents.");
        setLoadingDocs(false);
      }
    );
    const unsubFolders = listMyFolders(
      emailLower,
      (next) => {
        setFolders(next);
        setLoadingFolders(false);
      },
      () => setLoadingFolders(false)
    );
    return () => {
      unsubDocs();
      unsubFolders();
    };
  }, [emailLower]);

  const loading = loadingDocs || loadingFolders;
  const currentFolder = folders.find((f) => f.folderId === currentFolderId) ?? null;

  const canEditDoc = (d: DocMeta) =>
    d.ownerEmail === emailLower || (d.editorMembers ?? d.editors).includes(emailLower);
  const canEditFolder = (f: FolderMeta) =>
    f.ownerEmail === emailLower || (f.editorMembers ?? []).includes(emailLower);

  // What to show: a global flat search, or the current folder's children.
  const searching = query.trim().length > 0;
  const q = query.trim().toLowerCase();

  const shownFolders = useMemo(() => {
    const base = searching
      ? folders.filter((f) => f.name.toLowerCase().includes(q))
      : folders.filter((f) => (f.parentId ?? null) === currentFolderId);
    return [...base].sort((a, b) =>
      sort === "name"
        ? a.name.localeCompare(b.name)
        : (b.updatedAt?.toMillis?.() ?? 0) - (a.updatedAt?.toMillis?.() ?? 0)
    );
  }, [folders, searching, q, currentFolderId, sort]);

  const shownDocs = useMemo(() => {
    const base = searching
      ? docs.filter((d) => d.title.toLowerCase().includes(q))
      : docs.filter((d) => (d.folderId ?? null) === currentFolderId);
    return [...base].sort((a, b) =>
      sort === "name"
        ? a.title.localeCompare(b.title)
        : docSortValue(b, sort) - docSortValue(a, sort)
    );
  }, [docs, searching, q, currentFolderId, sort]);

  const trail: Crumb[] = useMemo(() => {
    const crumbs: Crumb[] = [{ id: null, name: "All documents" }];
    if (currentFolder) {
      for (const aid of currentFolder.ancestors ?? []) {
        const f = folders.find((x) => x.folderId === aid);
        if (f) crumbs.push({ id: f.folderId, name: f.name });
      }
      crumbs.push({ id: currentFolder.folderId, name: currentFolder.name });
    }
    return crumbs;
  }, [currentFolder, folders]);

  const onNewFolder = async (name: string) => {
    try {
      await createFolder(name, currentFolderId);
      success(`Created folder “${name}”.`);
    } catch (e) {
      toastError((e as Error).message);
    }
  };

  const onRename = async (name: string) => {
    if (!renameTarget) return;
    try {
      await renameItem(renameTarget.kind, renameTarget.id, name);
      success("Renamed.");
    } catch (e) {
      toastError((e as Error).message);
    }
  };

  const onDeleteFolder = async (f: FolderMeta) => {
    if (
      !window.confirm(
        `Delete folder “${f.name}”? Its contents move up to the parent folder.`
      )
    )
      return;
    try {
      await deleteFolder(f.folderId);
      success(`Deleted folder “${f.name}”.`);
    } catch (e) {
      toastError((e as Error).message);
    }
  };

  const folderMenu = (f: FolderMeta): MenuItem[] => {
    const items: MenuItem[] = [
      { label: "Open", icon: "folder-open", onSelect: () => navigate(`/folder/${f.folderId}`) },
    ];
    if (f.ownerEmail === emailLower) {
      items.push({ label: "Share…", icon: "share", onSelect: () => setShareFolder(f) });
    }
    if (canEditFolder(f)) {
      items.push({
        label: "Move…",
        icon: "move",
        onSelect: () =>
          setMoveTarget({
            kind: "folder",
            id: f.folderId,
            name: f.name,
            parentId: f.parentId ?? null,
          }),
      });
      items.push({
        label: "Rename…",
        icon: "edit",
        onSelect: () => setRenameTarget({ kind: "folder", id: f.folderId, name: f.name }),
      });
    }
    if (f.ownerEmail === emailLower) {
      items.push({ label: "Delete", icon: "trash", danger: true, onSelect: () => onDeleteFolder(f) });
    }
    return items;
  };

  const onDuplicate = async (d: DocMeta) => {
    try {
      const r = await duplicateDoc(d.docId, d.folderId ?? currentFolderId);
      success(`Duplicated as “${r.title}”.`);
      navigate(`/d/${r.docId}`);
    } catch (e) {
      toastError((e as Error).message);
    }
  };

  const docMenu = (d: DocMeta): MenuItem[] => {
    const items: MenuItem[] = [
      { label: "Open", icon: "file", onSelect: () => navigate(`/d/${d.docId}`) },
      { label: "Duplicate", icon: "copy", onSelect: () => onDuplicate(d) },
    ];
    if (canEditDoc(d)) {
      items.push({
        label: "Move…",
        icon: "move",
        onSelect: () =>
          setMoveTarget({
            kind: "doc",
            id: d.docId,
            name: d.title,
            parentId: d.folderId ?? null,
          }),
      });
      items.push({
        label: "Rename…",
        icon: "edit",
        onSelect: () => setRenameTarget({ kind: "doc", id: d.docId, name: d.title }),
      });
    }
    return items;
  };

  const empty = !loading && shownFolders.length === 0 && shownDocs.length === 0;

  return (
    <div className="workspace">
      {railOpen && (
        <div
          className="workspace-rail-backdrop"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setRailOpen(false);
          }}
        />
      )}
      <aside className={`workspace-rail${railOpen ? " is-open" : ""}`}>
        <button
          type="button"
          className="btn btn-primary workspace-upload"
          onClick={() => setUploadOpen(true)}
        >
          <Icon name="upload" size={18} />
          Upload
        </button>
        <FolderTree folders={folders} currentFolderId={currentFolderId} />
      </aside>

      <div className="workspace-main">
        <div className="workspace-head">
          <button
            type="button"
            className="btn btn-ghost workspace-rail-toggle"
            onClick={() => setRailOpen(true)}
            aria-label="Open folders"
            aria-expanded={railOpen}
          >
            <Icon name="folder" size={18} />
            Folders
          </button>
          <Breadcrumb trail={trail} />
          <div className="workspace-actions">
            <div className="search-box">
              <Icon name="search" size={16} className="search-icon" />
              <input
                className="input search-input"
                type="search"
                placeholder="Search…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search documents and folders"
              />
            </div>
            <select
              className="input sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value as Sort)}
              aria-label="Sort by"
            >
              <option value="recent">Recently updated</option>
              <option value="name">Name</option>
              <option value="views">Most viewed</option>
              <option value="viewed">Recently viewed</option>
            </select>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setNewFolderOpen(true)}
            >
              <Icon name="plus" size={18} />
              New folder
            </button>
          </div>
        </div>

        {error && (
          <div className="error-banner" role="alert">
            {error}
          </div>
        )}

        {loading ? (
          <CardSkeletons />
        ) : empty ? (
          <div className="empty-pane">
            <Icon name="folder-open" size={40} />
            <p className="empty-state">
              {searching
                ? "Nothing matches your search."
                : currentFolder
                  ? "This folder is empty."
                  : "No documents yet. Upload one to get started."}
            </p>
            {!searching && (
              <button type="button" className="btn btn-primary" onClick={() => setUploadOpen(true)}>
                <Icon name="upload" size={18} />
                Upload a document
              </button>
            )}
          </div>
        ) : (
          <div className="doc-grid">
            {shownFolders.map((f) => (
              <div key={f.folderId} className="card folder-card">
                <Link className="card-main" to={`/folder/${f.folderId}`}>
                  <span className="folder-card-icon">
                    <Icon name="folder" size={22} />
                  </span>
                  <h2 className="doc-card-title">{f.name}</h2>
                  <div className="doc-card-meta">
                    <span className="caption">Folder</span>
                    <span className="caption">Updated {formatTimestamp(f.updatedAt)}</span>
                  </div>
                </Link>
                <div className="card-menu">
                  <Menu
                    ariaLabel={`Actions for ${f.name}`}
                    triggerClassName="btn btn-ghost tap icon-btn"
                    trigger={<Icon name="more" />}
                    items={folderMenu(f)}
                  />
                </div>
              </div>
            ))}
            {shownDocs.map((d) => (
              <div key={d.docId} className="card doc-card">
                <Link className="card-main" to={`/d/${d.docId}`}>
                  <h2 className="doc-card-title">{d.title}</h2>
                  <div className="doc-card-meta">
                    <span className="caption num">{d.currentVersion}</span>
                    <span className="caption">Updated {formatTimestamp(d.updatedAt)}</span>
                  </div>
                  {/* Aggregate usage — on DocMeta.stats already, so this costs
                      no extra reads. Hidden until someone has actually opened
                      the doc, so a fresh upload doesn't advertise "0 views". */}
                  {!!d.stats?.views && (
                    <div className="doc-card-meta">
                      <span className="caption doc-card-views">
                        <Icon name="eye" size={14} />
                        {d.stats.views} view{d.stats.views === 1 ? "" : "s"}
                      </span>
                      <span className="caption">
                        {d.stats.viewers ?? 0} viewer
                        {(d.stats.viewers ?? 0) === 1 ? "" : "s"}
                      </span>
                      <span className="caption">
                        seen {formatRelative(d.stats.lastViewedAt)}
                      </span>
                    </div>
                  )}
                  <ConfidentialBadge compact />
                </Link>
                <div className="card-menu">
                  <Menu
                    ariaLabel={`Actions for ${d.title}`}
                    triggerClassName="btn btn-ghost tap icon-btn"
                    trigger={<Icon name="more" />}
                    items={docMenu(d)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {uploadOpen && (
        <UploadDialog
          folderId={currentFolderId}
          folderName={currentFolder?.name}
          onClose={() => setUploadOpen(false)}
          onUploaded={(docId) => {
            setUploadOpen(false);
            navigate(`/d/${docId}`);
          }}
        />
      )}

      {newFolderOpen && (
        <NameModal
          title="New folder"
          label="Folder name"
          submitLabel="Create"
          onClose={() => setNewFolderOpen(false)}
          onSubmit={async (name) => {
            setNewFolderOpen(false);
            await onNewFolder(name);
          }}
        />
      )}

      {renameTarget && (
        <NameModal
          title={`Rename ${renameTarget.kind}`}
          label="Name"
          initial={renameTarget.name}
          submitLabel="Rename"
          onClose={() => setRenameTarget(null)}
          onSubmit={async (name) => {
            setRenameTarget(null);
            await onRename(name);
          }}
        />
      )}

      {moveTarget && (
        <MoveDialog
          kind={moveTarget.kind}
          id={moveTarget.id}
          name={moveTarget.name}
          currentParentId={moveTarget.parentId}
          folders={folders}
          emailLower={emailLower}
          onClose={() => setMoveTarget(null)}
          onMoved={() => setMoveTarget(null)}
        />
      )}

      {shareFolder &&
        (() => {
          // Read from the live folders list so the rows reflect adds/removes as
          // the snapshot lands (shareFolder itself is a captured reference).
          const live =
            folders.find((f) => f.folderId === shareFolder.folderId) ?? shareFolder;
          return (
            <div
              className="share-modal-backdrop"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) setShareFolder(null);
              }}
            >
              <SharePanel
                title={`Share “${live.name}”`}
                note="People you add here can see everything in this folder and its subfolders."
                ownerEmail={live.ownerEmail}
                viewers={live.viewers}
                editors={live.editors}
                onApply={(c) => setFolderAccess(live.folderId, c)}
                onChange={() => {
                  /* live folder snapshot refreshes the rows + tree */
                }}
                onClose={() => setShareFolder(null)}
              />
            </div>
          );
        })()}
    </div>
  );
}

/** Small single-field modal for create/rename. */
function NameModal({
  title,
  label,
  initial = "",
  submitLabel,
  onClose,
  onSubmit,
}: {
  title: string;
  label: string;
  initial?: string;
  submitLabel: string;
  onClose: () => void;
  onSubmit: (name: string) => void;
}) {
  const [value, setValue] = useState(initial);
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!value.trim()}
            onClick={() => value.trim() && onSubmit(value.trim())}
          >
            {submitLabel}
          </button>
        </>
      }
    >
      <label className="field">
        <span className="field-label">{label}</span>
        {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
        <input
          className="input"
          value={value}
          autoFocus
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && value.trim()) onSubmit(value.trim());
          }}
        />
      </label>
    </Modal>
  );
}
